import { Button, Typography } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useAppAnalytics } from "../utils/useAppAnalytics";
import DrWeedyLogo from "../assets/DrWeedyLogo";
import PlanCheckout, { PlanCheckoutActivationCtx } from "../components/PlanCheckout";
import SalesClosedNotice from "../components/SalesClosedNotice";
import { useSalesOpen } from "../hooks/useSalesOpen";

export default function SubscribePage() {
  const { logout, setSubscription, user } = useAuth();
  const { trackEvent } = useAppAnalytics({ user });
  const salesClosed = useSalesOpen() === false;

  // O PlanCheckout não toca no AuthContext nem navega; a página decide o
  // pós-fluxo. Aqui reproduzimos o comportamento anterior: grava a assinatura
  // no contexto e, se já estiver ativa, vai para o chat.
  const handleActivated = (
    result: { status: string },
    ctx: PlanCheckoutActivationCtx,
  ) => {
    const planCode =
      ctx.plan === "annual" && ctx.annualBilling === "installments_12"
        ? "annual_12x"
        : ctx.plan;

    trackEvent("subscription_purchased", {
      plan_code: planCode,
      is_trial: ctx.isTrial,
      payment_status: result.status,
    });

    setSubscription({
      status: result.status as "active" | "pending",
      paymentUrl: null,
      isLifetime: ctx.plan === "lifetime",
      planKind: ctx.plan,
      isTrial: ctx.plan !== "lifetime" && ctx.isTrial,
    });

    if (result.status === "active") {
      window.location.href = "/chat";
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent"></div>

      <div className="w-full flex flex-col relative z-10">
        <div className="flex items-center justify-between px-8 py-4">
          <div className="flex items-center gap-3">
            <DrWeedyLogo width={40} height={40} />
            <Typography
              variant="h6"
              sx={{ color: "#ffffff", fontWeight: 700, letterSpacing: "-0.02em", fontFamily: "Oxanium, sans-serif" }}
            >
              Dr. Weedy
            </Typography>
          </div>
          <Button
            onClick={logout}
            variant="outlined"
            sx={{
              color: "rgba(255,255,255,0.85)",
              borderColor: "rgba(255,255,255,0.25)",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 600,
              borderRadius: "12px",
              padding: "6px 20px",
              "&:hover": {
                color: "#ffffff",
                borderColor: "rgba(255,255,255,0.5)",
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            Sair
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-lg w-full">
            {!salesClosed && (
              <div className="text-center mb-8">
                <Typography
                  variant="h3"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 700,
                    marginBottom: "12px",
                    letterSpacing: "-0.03em",
                    fontFamily: "Oxanium, sans-serif",
                  }}
                >
                  Assine o Dr. Weedy
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    lineHeight: 1.6,
                    fontWeight: 400,
                  }}
                >
                  Tenha acesso ao seu assistente com IA para profissionais da saúde, disponível 24/7
                </Typography>
              </div>
            )}

            {salesClosed ? (
              <SalesClosedNotice />
            ) : (
              <PlanCheckout
                availablePlans={["monthly", "annual", "lifetime"]}
                initialPlan="lifetime"
                initialTrial
                onActivated={handleActivated}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
